import { useEffect, useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, X, User, Lightning, Warning } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function CellPopover({
  routeCode,
  routeId,
  date,
  slot,
  slotLabel,
  currentStaffId,
  currentStaffAbsent, // {type, note} or null
  staffList,
  onAssign,
  onClear,
  children,
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState(null); // null=not fetched, []=fetched empty
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const currentName = useMemo(
    () => staffList.find((s) => s.id === currentStaffId)?.name || "",
    [currentStaffId, staffList]
  );

  // Auto-fetch suggestions when opened AND current staff is absent
  useEffect(() => {
    if (!open) return;
    if (!currentStaffAbsent) return;
    if (suggestions !== null) return;
    setLoadingSuggestions(true);
    api
      .replacements({ route_id: routeId, date, slot })
      .then((d) => setSuggestions(d.candidates || []))
      .catch(() => setSuggestions([]))
      .finally(() => setLoadingSuggestions(false));
  }, [open, currentStaffAbsent, routeId, date, slot, suggestions]);

  // Reset when closed
  useEffect(() => {
    if (!open) {
      setSuggestions(null);
      setSearch("");
    }
  }, [open]);

  const orderedStaff = useMemo(() => {
    const isDriver = slot === "driver";
    const priority = staffList.filter((s) =>
      isDriver ? s.role === "driver" : s.role === "loader"
    );
    const secondary = staffList.filter((s) =>
      isDriver ? s.role !== "driver" : s.role !== "loader"
    );
    return [...priority, ...secondary];
  }, [staffList, slot]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-80 p-0 border-2 border-black rounded-none shadow-[4px_4px_0_0_#0A0A0A]"
        data-testid="cell-popover"
      >
        <div className="px-3 py-2 border-b-2 border-black bg-black text-white">
          <div className="font-heading font-bold text-xs tracking-widest">
            {routeCode} · {slotLabel}
          </div>
          <div className="font-mono-plex text-[10px] text-zinc-400 mt-0.5">{date}</div>
        </div>

        {currentStaffAbsent && (
          <div className="px-3 py-2 bg-rose-50 border-b border-rose-300 flex items-start gap-2">
            <Warning size={14} weight="bold" className="text-rose-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-mono-plex text-[10px] tracking-widest text-rose-800 font-bold">
                {currentName} — ON {currentStaffAbsent.type?.toUpperCase()}
              </div>
              {currentStaffAbsent.note && (
                <div className="font-mono-plex text-[10px] text-rose-700 mt-0.5">
                  {currentStaffAbsent.note}
                </div>
              )}
            </div>
          </div>
        )}

        <Command shouldFilter={true}>
          <CommandInput
            placeholder="Search staff…"
            value={search}
            onValueChange={setSearch}
            data-testid="cell-popover-search"
            className="font-mono-plex text-xs"
          />
          <CommandList className="max-h-72">
            <CommandEmpty>No staff found.</CommandEmpty>

            {/* Replacement suggestions */}
            {currentStaffAbsent && (
              <CommandGroup
                heading={
                  <span className="flex items-center gap-1.5">
                    <Lightning size={11} weight="fill" className="text-amber-600" />
                    SUGGESTED REPLACEMENTS
                  </span>
                }
              >
                {loadingSuggestions && (
                  <div className="px-3 py-2 font-mono-plex text-[10px] text-zinc-500 tracking-widest">
                    FINDING AVAILABLE STAFF…
                  </div>
                )}
                {!loadingSuggestions && suggestions && suggestions.length === 0 && (
                  <div className="px-3 py-2 font-mono-plex text-[10px] text-zinc-500 tracking-widest">
                    NO AVAILABLE STAFF FOR THIS DAY
                  </div>
                )}
                {!loadingSuggestions &&
                  suggestions &&
                  suggestions.slice(0, 6).map((c) => (
                    <CommandItem
                      key={`sugg-${c.id}`}
                      value={`__sugg__ ${c.name}`}
                      onSelect={() => {
                        onAssign(c.id);
                        setOpen(false);
                      }}
                      data-testid={`suggest-${c.id}`}
                      className="font-mono-plex text-xs !bg-amber-50 aria-selected:!bg-amber-200"
                    >
                      <Lightning
                        size={12}
                        weight="fill"
                        className="mr-2 text-amber-600 shrink-0"
                      />
                      <span className="flex-1">{c.name}</span>
                      <span
                        className={`text-[9px] uppercase tracking-widest ${
                          c.role_match ? "text-emerald-700" : "text-zinc-500"
                        }`}
                      >
                        {c.role}
                        {c.role_match ? " ✓" : ""}
                      </span>
                      <span className="text-[9px] text-zinc-500 ml-2 font-semibold">
                        {c.workload_7d}d/wk
                      </span>
                    </CommandItem>
                  ))}
              </CommandGroup>
            )}

            {currentStaffId && (
              <CommandGroup heading="Actions">
                <CommandItem
                  onSelect={() => {
                    onClear();
                    setOpen(false);
                  }}
                  data-testid="clear-slot"
                  className="text-red-700 font-mono-plex text-xs"
                >
                  <X size={14} weight="bold" className="mr-2" />
                  CLEAR — {currentName}
                </CommandItem>
              </CommandGroup>
            )}

            <CommandGroup heading={slot === "driver" ? "All Drivers" : "All Loaders"}>
              {orderedStaff.map((s) => (
                <CommandItem
                  key={s.id}
                  value={s.name}
                  onSelect={() => {
                    onAssign(s.id);
                    setOpen(false);
                  }}
                  data-testid={`assign-${s.id}`}
                  className="font-mono-plex text-xs"
                >
                  <User size={14} className="mr-2 text-zinc-500 shrink-0" />
                  <span className="flex-1">{s.name}</span>
                  <span className="text-[9px] uppercase tracking-widest text-zinc-500">
                    {s.role}
                  </span>
                  {currentStaffId === s.id && (
                    <Check size={14} weight="bold" className="ml-1 text-black" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
